import { useCallback, useId, useMemo, useState } from "react";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandLoading,
} from "cmdk";
import { Check, ChevronsUpDown } from "lucide-react";

import { cn } from "@/lib/utils";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

export interface ComboboxOption<TValue> {
  label: string;
  value: TValue;
  disabled?: boolean;
}

export interface ComboboxProps<TValue> {
  /**
   * The full list of options to render.
   *
   * For a static, in-memory list, pass every option up front and leave
   * `inputValue`/`onInputValueChange` undefined — the component will filter
   * them itself (via cmdk's built-in fuzzy matching on `label`).
   *
   * For an async/backend-driven list (e.g. a React Query hook that searches
   * users/organisations/devices as the user types), pass `inputValue` +
   * `onInputValueChange` so the caller owns the search text, and pass in
   * `options` that are already filtered server-side — the component will
   * trust that list as-is and will NOT re-filter it locally.
   */
  options: ComboboxOption<TValue>[];
  /** Currently selected value, or `null`/`undefined` for "nothing selected". */
  value?: TValue | null;
  /** Called with the newly selected value, or `null` when the selection is cleared. */
  onValueChange?: (value: TValue | null) => void;
  /**
   * Controlled search text. Provide this (together with `onInputValueChange`)
   * to hand search-text ownership to the caller — required for async/backend
   * filtering. Omit both for a simple static list with internal filtering.
   */
  inputValue?: string;
  /** Called with the new search text as the user types. See `inputValue`. */
  onInputValueChange?: (value: string) => void;
  /** Placeholder shown on the trigger button when nothing is selected. */
  placeholder?: string;
  /** Placeholder shown inside the search input. */
  searchPlaceholder?: string;
  /** Message shown when there are no matching options. */
  emptyText?: string;
  /** Shows a loading indicator instead of the empty state (e.g. while an async search is in flight). */
  loading?: boolean;
  /** Disables the trigger entirely. */
  disabled?: boolean;
  /** Custom equality check for matching `value` against `options[].value`. Defaults to `===`. */
  isOptionEqual?: (a: TValue, b: TValue) => boolean;
  /** Additional class names for the trigger button. */
  className?: string;
  /** Additional class names for the popover content. */
  contentClassName?: string;
  /** `name`/`id` passed through to the trigger button, useful for labels. */
  name?: string;
  id?: string;
}

export function Combobox<TValue>({
  options,
  value = null,
  onValueChange,
  inputValue,
  onInputValueChange,
  placeholder = "Select an option...",
  searchPlaceholder = "Search...",
  emptyText = "No results found.",
  loading = false,
  disabled = false,
  isOptionEqual,
  className,
  contentClassName,
  name,
  id,
}: ComboboxProps<TValue>) {
  const [open, setOpen] = useState(false);
  const generatedId = useId();
  const triggerId = id ?? generatedId;

  // Controlled search text implies an externally-owned (typically async)
  // options list — trust it as-is instead of re-filtering locally.
  const isExternallyFiltered = inputValue !== undefined;

  const isEqual = useCallback(
    (a: TValue, b: TValue) => (isOptionEqual ? isOptionEqual(a, b) : a === b),
    [isOptionEqual],
  );

  const selectedOption = useMemo(
    () =>
      value === null || value === undefined
        ? null
        : (options.find((option) => isEqual(option.value, value)) ?? null),
    [options, value, isEqual],
  );

  function handleSelect(option: ComboboxOption<TValue>) {
    const isSame =
      selectedOption !== null && isEqual(option.value, selectedOption.value);
    onValueChange?.(isSame ? null : option.value);
    setOpen(false);
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          id={triggerId}
          name={name}
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            "flex h-10 w-full items-center justify-between whitespace-nowrap rounded-md border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm transition-colors disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-700 dark:bg-slate-950",
            "outline-none focus:ring-2 focus:ring-brand-primary focus:ring-offset-2 focus:ring-offset-white dark:focus:ring-offset-slate-950",
            "aria-invalid:border-red-500 aria-invalid:focus:ring-red-500",
            selectedOption
              ? "text-slate-900 dark:text-slate-100"
              : "text-slate-400 dark:text-slate-500",
            className,
          )}
        >
          <span className="truncate">
            {selectedOption ? selectedOption.label : placeholder}
          </span>
          <ChevronsUpDown className="ml-2 size-4 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={4}
        className={cn(
          "w-(--radix-popover-trigger-width) overflow-hidden p-0",
          contentClassName,
        )}
      >
        <Command
          shouldFilter={!isExternallyFiltered}
          className="flex w-full flex-col overflow-hidden"
        >
          <div className="flex items-center border-b border-slate-200 px-3 dark:border-slate-800">
            <CommandInput
              value={inputValue}
              onValueChange={onInputValueChange}
              placeholder={searchPlaceholder}
              className="flex h-9 w-full rounded-md bg-transparent py-3 text-sm text-slate-900 outline-none placeholder:text-slate-400 disabled:cursor-not-allowed disabled:opacity-50 dark:text-slate-100 dark:placeholder:text-slate-500"
            />
          </div>
          <CommandList className="max-h-64 overflow-y-auto overflow-x-hidden p-1">
            {loading ? (
              <CommandLoading>
                <div className="py-6 text-center text-sm text-slate-500 dark:text-slate-400">
                  Loading...
                </div>
              </CommandLoading>
            ) : (
              <CommandEmpty className="py-6 text-center text-sm text-slate-500 dark:text-slate-400">
                {emptyText}
              </CommandEmpty>
            )}
            <CommandGroup>
              {options.map((option, index) => {
                const isSelected =
                  selectedOption !== null &&
                  isEqual(option.value, selectedOption.value);
                return (
                  <CommandItem
                    key={index}
                    value={option.label}
                    disabled={option.disabled}
                    onSelect={() => handleSelect(option)}
                    className="relative flex cursor-default select-none items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none aria-selected:bg-slate-100 aria-selected:text-slate-900 data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-50 dark:aria-selected:bg-slate-800 dark:aria-selected:text-slate-100"
                  >
                    <Check
                      className={cn(
                        "size-4 shrink-0",
                        isSelected ? "opacity-100" : "opacity-0",
                      )}
                    />
                    <span className="truncate">{option.label}</span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
